import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { documentApi, employeeApi } from '../../services/api';
import { EmptyState, Spinner, Modal, ConfirmModal } from '../../components/shared/index';
import {
  PlusIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const TYPES = ['contract','policy','offer_letter','id_document','nda','other'];

const TYPE_LABELS = {
  contract:     'Contracts',
  policy:       'Policies',
  offer_letter: 'Offer letters',
  id_document:  'ID documents',
  nda:          'NDAs',
  other:        'Other',
};

function bytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function storageUrl(path) {
  if (!path) return '#';
  const base = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  return `${base}/storage/${path.replace(/^\/+/, '')}`;
}

function UploadModal({ open, onClose, onSubmit, pending, employees }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('contract');
  const [employeeId, setEmployeeId] = useState('');
  const [visible, setVisible] = useState(true);

  const reset = () => { setFile(null); setName(''); setType('contract'); setEmployeeId(''); setVisible(true); };

  const submit = () => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('name', name || file.name);
    form.append('type', type);
    if (employeeId) form.append('employee_id', employeeId);
    form.append('is_employee_visible', visible ? 1 : 0);
    onSubmit(form);
  };

  const valid = file && (name || file?.name);

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Upload document" size="md">
      <div className="space-y-4">
        <div>
          <label className="label">File</label>
          <input type="file" className="input" onChange={e => {
            const f = e.target.files?.[0] || null;
            setFile(f);
            if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
          }} />
          {file && <p className="text-xs text-gray-500 mt-1">{bytes(file.size)} · {file.type || 'unknown type'}</p>}
        </div>
        <div>
          <label className="label">Document name</label>
          <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 2026 Employment contract" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Type</label>
            <select className="input capitalize" value={type} onChange={e => setType(e.target.value)}>
              {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Assign to employee (optional)</label>
            <select className="input" value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
              <option value="">Tenant-wide</option>
              {(employees || []).map(em => <option key={em.id} value={em.id}>{em.name}</option>)}
            </select>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} />
          Visible to the assigned employee
        </label>
        <div className="flex justify-end gap-3">
          <button onClick={() => { reset(); onClose(); }} className="btn-secondary">Cancel</button>
          <button disabled={!valid || pending} onClick={submit} className="btn-primary disabled:opacity-40">
            {pending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SignModal({ open, onClose, onSubmit, pending, doc }) {
  const { user } = useAuth();
  const [typedName, setTypedName] = useState('');
  const valid = typedName.trim().toLowerCase() === (user?.name || '').trim().toLowerCase() && typedName.length > 0;

  return (
    <Modal open={open} onClose={() => { setTypedName(''); onClose(); }} title="Sign document" size="md">
      <div className="space-y-4">
        <div className="border border-gray-200 rounded-xl p-6 bg-gray-50 text-center">
          <DocumentTextIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="font-semibold text-gray-800">{doc?.name}</p>
          <p className="text-xs text-gray-500 mt-1 capitalize">{doc?.type?.replace('_',' ')} · v{doc?.version}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          By typing your full legal name below and clicking "Sign", you acknowledge this as your legally binding electronic signature. A SHA-256 hash of the document, your user ID and the timestamp will be stored with your signature.
        </div>
        <div>
          <label className="label">Type your full name to sign</label>
          <input
            className={clsx('input text-lg font-serif', valid && 'border-green-500')}
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
            placeholder={user?.name}
          />
          <p className="text-xs mt-1 text-gray-500">Must match your account name ({user?.name}).</p>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => { setTypedName(''); onClose(); }} className="btn-secondary">Cancel</button>
          <button
            disabled={!valid || pending}
            onClick={() => onSubmit({ signer_name: typedName })}
            className="btn-primary disabled:opacity-40"
          >
            {pending ? 'Signing…' : 'Sign document'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SignaturesPanel({ doc, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['doc-signatures', doc?.id],
    queryFn:  () => documentApi.signatures(doc.id).then(r => r.data),
    enabled:  !!doc?.id,
  });

  return (
    <Modal open={!!doc} onClose={onClose} title={`Signatures — ${doc?.name || ''}`} size="md">
      {isLoading ? <div className="flex justify-center py-10"><Spinner /></div>
      : (data || []).length === 0
        ? <p className="text-sm text-gray-500 text-center py-8">No signatures yet.</p>
        : (
          <ul className="divide-y divide-gray-100">
            {(data || []).map(s => (
              <li key={s.id} className="py-3 flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-semibold">
                  {(s.signer_name || s.signer?.name || '?')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{s.signer_name || s.signer?.name}</p>
                  <p className="text-xs text-gray-500">{s.signer?.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Signed {new Date(s.signed_at).toLocaleString()} · IP {s.ip_address || 'unknown'}
                  </p>
                  <p className="text-[10px] text-gray-400 font-mono break-all mt-1">SHA-256 {s.signature_hash}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
    </Modal>
  );
}

export default function DocumentsTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ['hr_admin','super_admin'].includes(user?.role);

  const [typeFilter, setTypeFilter] = useState('');
  const [empFilter, setEmpFilter] = useState('');
  const [openUpload, setOpenUpload] = useState(false);
  const [signDoc, setSignDoc] = useState(null);
  const [viewSigs, setViewSigs] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { data: docs, isLoading } = useQuery({
    queryKey: ['documents', typeFilter, empFilter],
    queryFn:  () => documentApi.list({ type: typeFilter || undefined, employee_id: empFilter || undefined }).then(r => r.data),
  });

  const { data: employees } = useQuery({
    queryKey: ['employees','all-lite'],
    queryFn:  () => employeeApi.list().then(r => {
      const arr = r.data?.data || r.data;
      return Array.isArray(arr) ? arr : [];
    }),
    enabled: isAdmin,
  });

  const upload = useMutation({
    mutationFn: documentApi.upload,
    onSuccess:  () => { toast.success('Document uploaded'); setOpenUpload(false); qc.invalidateQueries({ queryKey: ['documents'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Upload failed'),
  });

  const sign = useMutation({
    mutationFn: ({ id, data }) => documentApi.sign(id, data),
    onSuccess:  () => { toast.success('Signature recorded'); setSignDoc(null); qc.invalidateQueries({ queryKey: ['documents'] }); qc.invalidateQueries({ queryKey: ['doc-signatures'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Signing failed'),
  });

  const del = useMutation({
    mutationFn: (id) => documentApi.delete(id),
    onSuccess:  () => { toast.success('Document archived'); qc.invalidateQueries({ queryKey: ['documents'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Delete failed'),
  });

  const grouped = useMemo(() => {
    const g = {};
    (docs || []).forEach(d => {
      (g[d.type] ||= []).push(d);
    });
    return g;
  }, [docs]);

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <select className="input text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
          {isAdmin && (
            <select className="input text-sm" value={empFilter} onChange={e => setEmpFilter(e.target.value)}>
              <option value="">All employees</option>
              {(employees || []).map(em => <option key={em.id} value={em.id}>{em.name}</option>)}
            </select>
          )}
        </div>
        <button onClick={() => setOpenUpload(true)} className="btn-primary">
          <PlusIcon className="w-4 h-4" /> Upload document
        </button>
      </div>

      {(docs || []).length === 0
        ? <EmptyState title="No documents yet" description="Upload contracts, policies and offer letters to keep everything in one place." />
        : (
          <div className="space-y-8">
            {TYPES.filter(t => grouped[t]?.length).map(t => (
              <section key={t}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="section-title">{TYPE_LABELS[t]}</h3>
                  <span className="text-xs text-gray-400">{grouped[t].length} file{grouped[t].length !== 1 ? 's' : ''}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {grouped[t].map(d => (
                    <div key={d.id} className="card p-4 flex flex-col">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center shrink-0">
                          <DocumentTextIcon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-900 text-sm truncate">{d.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            v{d.version} · {bytes(d.file_size)} · {new Date(d.created_at).toLocaleDateString()}
                          </p>
                          {d.employee && <p className="text-xs text-gray-500 mt-1">For {d.employee.name}</p>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <ShieldCheckIcon className="w-3.5 h-3.5" />
                          {(d.signatures?.length || 0)} signature{(d.signatures?.length || 0) !== 1 ? 's' : ''}
                        </div>
                        <div className="flex gap-1">
                          <a href={storageUrl(d.file_path)} target="_blank" rel="noreferrer"
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-600" title="Download">
                            <ArrowDownTrayIcon className="w-4 h-4" />
                          </a>
                          <button onClick={() => setViewSigs(d)} className="p-1.5 rounded hover:bg-gray-100 text-gray-600" title="View signatures">
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button onClick={() => setSignDoc(d)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Sign">
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          {isAdmin && (
                            <button onClick={() => setConfirmDelete(d)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

      <UploadModal
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        onSubmit={(form) => upload.mutate(form)}
        pending={upload.isPending}
        employees={employees}
      />

      <SignModal
        open={!!signDoc}
        doc={signDoc}
        onClose={() => setSignDoc(null)}
        onSubmit={(data) => sign.mutate({ id: signDoc.id, data })}
        pending={sign.isPending}
      />

      <SignaturesPanel doc={viewSigs} onClose={() => setViewSigs(null)} />

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => del.mutate(confirmDelete.id)}
        title="Archive document?"
        message={`"${confirmDelete?.name}" will be soft-deleted. Signatures remain on record.`}
        danger
      />
    </div>
  );
}
